// Generated by tutorials/record.cjs from tutorials/articles/h34-guests.cjs.
// Do not edit: change the script and run `node tutorials/record.cjs h34 --text-only` (wording) or
// `node tutorials/record.cjs h34` (pictures and clips).
import type { HelpArticle } from '../types';

const article: HelpArticle = {
  slug: 'guests',
  catalogId: 'h34',
  title: 'Guests',
  summary: 'A guest eats with a resident: a family member at Sunday lunch, or a spouse every morning. Add a guest from their resident, set when they come, find them again, and end their visits.',
  audience: 'Dietary managers and dietary staff who set up visitors\' meals',
  area: 'residents',
  learn: [
    'Add a guest for a resident and set when they eat with you',
    'Give a guest a diet order',
    'Find a guest again, including older guests',
    'Change a guest\'s visits, end them, or delete the guest',
    'Where guests are counted: the dashboard and Census',
  ],
  keywords: [
    'guest',
    'guests',
    'visitor',
    'family',
    'guest meal',
    'guest tray',
    'new guest',
    'quick add guest',
    'guest profile',
    'schedule',
    'recurrence',
    'one time',
    'every week',
    'end by',
    'past guests',
    'older guests',
    'see guests',
    'guests today',
    'census',
    'delete guest',
    'show discharged',
  ],
  steps: [
    {
      id: 'new-guest',
      part: 'Add a guest',
      heading: 'Start from the resident',
      text: 'A guest always belongs to a resident: the person they are visiting. On Residents, select the resident\'s row (click the row, not the name, which opens the record).\n\nThen select More actions (the three dots at the right of the toolbar) and New Guest.',
      media: {
        image: {
          src: '/help/guests/new-guest.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/guests/new-guest.mp4',
          width: 1280,
          height: 748,
          durationSec: 4.1,
        },
        alt: 'The Residents list with Achebe, Raymond selected and the More actions menu open, pointing at New Guest.',
      },
    },
    {
      id: 'name',
      part: 'Add a guest',
      heading: 'Name the guest',
      text: 'The Guest Profile opens. Select Primary Info on the left.\n\nFirst Name starts as GUEST and Last Name as the resident\'s last name, so you can save right away when you do not know the name. Type the guest\'s real names over them if you have them. Names are kept in capitals.\n\nBirth Date is optional.',
      media: {
        image: {
          src: '/help/guests/name.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/guests/name.mp4',
          width: 1280,
          height: 748,
          durationSec: 5.9,
        },
        alt: 'The Guest Profile for ACHEBE, GRACE on Primary Info: First Name GRACE, Last Name ACHEBE, Birth Date blank, and Allow Selective Menu Selections.',
      },
    },
    {
      id: 'when',
      part: 'Add a guest',
      heading: 'Say when they eat with you',
      text: 'Select Schedule. Check each meal the guest eats: Breakfast, Lunch, Dinner. At least one is needed.\n\nOne Time is a single visit on the Date you pick. Recurrence is a guest who comes regularly: Daily (every few days, or every weekday) or Weekly on the days you check. Pick the Start date, and an End by date if you know when the visits stop.',
      media: {
        image: {
          src: '/help/guests/when.webp',
          width: 1190,
          height: 736,
        },
        clip: {
          src: '/help/guests/when.mp4',
          width: 1190,
          height: 736,
          durationSec: 6,
        },
        alt: 'The Schedule section with Lunch checked, Recurrence and Weekly selected, Recur every 1 week(s) on Sun, Start today and No end date.',
      },
    },
    {
      id: 'diet',
      part: 'Add a guest',
      heading: 'Check the diet order',
      text: 'Select Diet Order. Diet Type 1 starts as your facility\'s house diet. Change it, and add a consistency, when the guest needs something else.\n\nThe diet order shows under the guest\'s name at the top, as it does for a resident.',
      media: {
        image: {
          src: '/help/guests/diet.webp',
          width: 1280,
          height: 748,
        },
        alt: 'The Diet Order section with Diet Type 1 Regular and the consistency boxes empty, and Regular under ACHEBE, GRACE in the header.',
      },
    },
    {
      id: 'save',
      part: 'Add a guest',
      heading: 'Save',
      text: 'Select Save Changes at the top right. The number on it counts the sections you changed. It stays greyed out until the guest has a name and a schedule with at least one meal.\n\nYou are back on Residents, with the new guest listed and selected under the resident.',
      media: {
        image: {
          src: '/help/guests/save.webp',
          width: 1280,
          height: 156,
        },
        alt: 'Residents with Achebe, Raymond opened: Guests (1) lists ACHEBE, GRACE, selected, with the next date and Mealtime(s): Lunch.',
      },
    },
    {
      id: 'under-resident',
      part: 'Find a guest again',
      heading: 'Under their resident',
      text: 'A resident with guests has a small guest count beside their name, and an arrow at the start of the row. Select the arrow, then Guests, to list them: each one\'s next visit (or last visit, for a guest who has been) and the meals. The circled arrows mark a guest who comes regularly; point at them to see the schedule.\n\nThis list keeps a guest for 7 days after their last visit. Older guests are left out, and a line says how many, with a link to the Guests page.',
      media: {
        image: {
          src: '/help/guests/under-resident.webp',
          width: 1280,
          height: 192,
        },
        clip: {
          src: '/help/guests/under-resident.mp4',
          width: 1280,
          height: 192,
          durationSec: 2,
        },
        alt: 'Okafor, Harold opened on Residents: Guests (1) lists OKAFOR, HELEN with her last visit and Lunch, then 1 older guest hidden. See Guests.',
      },
    },
    {
      id: 'guests-page',
      part: 'Find a guest again',
      heading: 'Every guest: the Guests page',
      text: 'Select See Guests, or Guests under Residents in the menu. This page lists every guest, past and coming, with their resident and room.\n\nStatus says Today (they eat with you today), Upcoming, Past, or Not scheduled. Date is the next visit, or the last one for a past guest. Meals today shows B, L and D for today\'s meals, and Recurrence describes a regular guest.',
      media: {
        image: {
          src: '/help/guests/guests-page.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/guests/guests-page.mp4',
          width: 1280,
          height: 748,
          durationSec: 2.5,
        },
        alt: 'The Guests page listing seven guests: Today, Upcoming and Past, with Resident, Room, Status, Date, Meals today and Recurrence.',
      },
    },
    {
      id: 'filter',
      part: 'Find a guest again',
      heading: 'Narrow the list',
      text: 'The buttons along the top show only Today\'s, Upcoming or Past guests; the numbers say how many of each. All shows everyone again.\n\nType in Find guests to look for a guest or a resident by name. Show discharged adds the guests of discharged residents.',
      media: {
        image: {
          src: '/help/guests/filter.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/guests/filter.mp4',
          width: 1280,
          height: 748,
          durationSec: 2.5,
        },
        alt: 'The Past button selected, listing OKAFOR, HELEN and OKAFOR, ROBERT, each with Last visit and the date.',
      },
    },
    {
      id: 'open',
      part: 'Change or end a guest',
      heading: 'Open the guest',
      text: 'Select the guest, then Open (or double-click it). On Residents, click the guest\'s name in the list under the resident.\n\nOverview shows the guest at a glance: name, diet order, allergies and the schedule. Each Edit link goes to that section.',
      media: {
        image: {
          src: '/help/guests/open.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/guests/open.mp4',
          width: 1280,
          height: 748,
          durationSec: 3.9,
        },
        alt: 'The Guest Profile for ELLISON, DANIEL on Overview, with the Schedule card: Lunch, Weekly every week on Sun, and the start date with no end date.',
      },
    },
    {
      id: 'end',
      part: 'Change or end a guest',
      heading: 'Change or end the visits',
      text: 'Select Schedule and change what you need: the meals, the days, or the dates.\n\nTo end a regular guest\'s visits, pick End by and the date of the last visit, then save. After that date the guest is no longer expected, and moves to Past.',
      media: {
        image: {
          src: '/help/guests/end.webp',
          width: 1280,
          height: 748,
        },
        clip: {
          src: '/help/guests/end.mp4',
          width: 1280,
          height: 748,
          durationSec: 4,
        },
        alt: 'The Schedule section for ELLISON, DANIEL with End by picked and a date filled in, and Save Changes (1) at the top.',
      },
    },
    {
      id: 'delete',
      part: 'Change or end a guest',
      heading: 'Delete a guest',
      text: 'A guest added by mistake can be deleted. On Residents, open the resident\'s guest list and click the guest\'s row (not the name), so the guest is selected. Then select More actions and Delete, and confirm.\n\nThere is no need to delete past guests: they simply stop showing under the resident.',
      media: {
        image: {
          src: '/help/guests/delete.webp',
          width: 708,
          height: 378,
        },
        clip: {
          src: '/help/guests/delete.mp4',
          width: 708,
          height: 378,
          durationSec: 2,
        },
        alt: 'The Delete Guest box asking Are you sure you want to delete this guest?, for OKAFOR, HELEN, with Cancel and Delete.',
      },
    },
    {
      id: 'dashboard',
      part: 'Where guests are counted',
      heading: 'On the dashboard',
      text: 'The Census number at the top of the dashboard adds today\'s guests to your residents, and the line under it splits the two.',
      media: {
        image: {
          src: '/help/guests/dashboard.webp',
          width: 1280,
          height: 136,
        },
        alt: 'The dashboard\'s top row: Census with the residents and 2 guests under it.',
      },
    },
    {
      id: 'dashboard-card',
      part: 'Where guests are counted',
      heading: 'The Guests card',
      text: 'Further down the dashboard, the Guests card lists the guests added in the time span picked at the top, such as the last 72 hours, newest first. Click a guest to open their profile.',
      media: {
        image: {
          src: '/help/guests/dashboard-card.webp',
          width: 816,
          height: 324,
        },
        alt: 'The dashboard\'s Guests card listing the guests added in the last 72 hours, with the date each was added.',
      },
    },
    {
      id: 'census',
      part: 'Where guests are counted',
      heading: 'On Census',
      text: 'Residents, Census has its own Guests Today: how many guests eat with you today, and how many at each meal. They are not part of the resident totals above them.\n\nSee all guests opens the Guests page.',
      media: {
        image: {
          src: '/help/guests/census.webp',
          width: 1280,
          height: 202,
        },
        alt: 'Census, Guests Today: Total, Breakfast, Lunch and Dinner counts, and the See all guests link.',
      },
    },
    {
      id: 'orders',
      part: 'Where guests are counted',
      heading: 'Taking a guest\'s meal order',
      text: 'Check Allow Selective Menu Selections, on the guest\'s Primary Info, when staff should take this guest\'s menu choices in Selective Touch, as they do for a resident.\n\nFor a visitor who turns up at mealtime without a profile, use Add Guest in Selective Touch instead. See Selective Touch.',
      textOnly: true,
    },
  ],
  tips: [
    'Quick Add Guest, on the same More actions menu, adds one or more guests for the resident in one go: pick how many, the meals and the dates. Each one gets a profile you can open and fill in later.',
    'A visitor who turns up at mealtime can be added right there in Selective Touch, with Add Guest. That guest is for that meal only. See Selective Touch.',
    'On the Guests page, Find guests looks for the guest\'s name and the resident\'s name, so typing the family name finds everyone visiting that resident.',
  ],
  notes: [
    'Food allergies on a guest\'s profile are a reminder printed on the tray ticket. To keep a food off the tray, add it under Adjustments as well. See Adjustments.',
    'Guests are not counted in your resident census. They have their own Guests Today numbers.',
    'Adding, changing and deleting guests each need the right role. If New Guest or Save is greyed out or missing, ask your administrator.',
  ],
  related: ['selective-touch', 'adjustments', 'finding-a-resident', 'your-dashboard', 'tray-tickets'],
  recorded: '2026-11-03',
};

export default article;
